import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Browser } from '@capacitor/browser';
import { auth } from '../firebase/config';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  AuthCredential,
  signOut,
  signInWithCredential
} from 'firebase/auth';

class CapacitorAuthService {
  private googleProvider = new GoogleAuthProvider();
  private appleProvider = new OAuthProvider('apple.com');
  private initialized = false;

  constructor() {
    this.initialize();
  }

  private async initialize() {
    if (this.initialized) return;

    try {
      this.googleProvider.addScope('profile');
      this.googleProvider.addScope('email');

      this.appleProvider.addScope('email');
      this.appleProvider.addScope('name');

      await App.addListener('appUrlOpen', ({ url }) => {
        this.handleDeepLink(url);
      });

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize CapacitorAuthService:', error);
    }
  }

  private async isNativePlatform() {
    const info = await Device.getInfo();
    return info.platform === 'android' || info.platform === 'ios';
  }

  async signIn(email: string, password: string): Promise<string> {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user.getIdToken();
  }

  async register(email: string, password: string): Promise<string> {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user.getIdToken();
  }

  async signInWithGoogle(): Promise<string> {
    const isNative = await this.isNativePlatform();

    try {
      if (isNative) {
        const authEndpoint = 'https://accounts.google.com/o/oauth2/v2/auth';
        const clientId = auth.app.options.apiKey;

        const params = new URLSearchParams({
          client_id: clientId,
          redirect_uri: 'com.estoicogoals.app://oauth/googleflow',
          response_type: 'token',
          scope: 'email profile',
        });

        const url = `${authEndpoint}?${params.toString()}`;

        await Browser.open({ url });

        return new Promise((resolve, reject) => {
          const unsubscribe = App.addListener('appUrlOpen', async ({ url }) => {
            if (url.includes('access_token=')) {
              unsubscribe.remove();
              await Browser.close();

              const token = new URLSearchParams(url.split('#')[1]).get('access_token');
              if (!token) {
                reject(new Error('No token received'));
                return;
              }

              const credential = GoogleAuthProvider.credential(null, token);
              const result = await signInWithCredential(auth, credential);
              resolve(result.user.getIdToken());
            }
          });
        });
      } else {
        const result = await signInWithPopup(auth, this.googleProvider);
        return result.user.getIdToken();
      }
    } catch (error) {
      console.error('Google Sign-In failed:', error);
      throw error;
    }
  }

  async signInWithApple(): Promise<string> {
    const isNative = await this.isNativePlatform();

    try {
      if (isNative) {
        const authEndpoint = 'https://appleid.apple.com/auth/authorize';
        const clientId = auth.app.options.apiKey;

        const params = new URLSearchParams({
          client_id: clientId,
          redirect_uri: 'com.estoicogoals.app://oauth/googleflow',
          response_type: 'code id_token',
          scope: 'email name',
          response_mode: 'fragment',
        });

        const url = `${authEndpoint}?${params.toString()}`;
        await Browser.open({ url });

        return new Promise((resolve, reject) => {
          const unsubscribe = App.addListener('appUrlOpen', async ({ url }) => {
            if (url.includes('id_token=')) {
              unsubscribe.remove();
              await Browser.close();

              const idToken = new URLSearchParams(url.split('#')[1]).get('id_token');
              if (!idToken) {
                reject(new Error('No token received'));
                return;
              }

              const credential = OAuthProvider.credential('apple.com', idToken);
              const result = await signInWithCredential(auth, credential);
              resolve(result.user.getIdToken());
            }
          });
        });
      } else {
        const result = await signInWithPopup(auth, this.appleProvider);
        return result.user.getIdToken();
      }
    } catch (error) {
      console.error('Apple Sign-In failed:', error);
      throw error;
    }
  }

  // todo: wtf is this?
  private async handleDeepLink(url: string) {
    console.log('Deep link received:', url);
  }
}

export const capacitorAuth = new CapacitorAuthService();
